/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useBenefits } from "api/quoterApi";
import { useDeductibleOptions } from "hooks/CoverageQueryHook";
import { benefitOptions, preferreds } from "helpers/BenefitOptionsValues";
import { arrayIncludesNumber } from "helpers/toolsHelper";
import { filterBenefitsByBenefitId, getValueByBenefitId, hasDependantInCountry } from "components/utils/helper";
import { getDeductibleIgnoredOptions, isLegacyQuotedContext } from "helpers/DeductibleRules";

/**
 * Benefit-selection logic (which options are valid to show, which are
 * auto-preferred, and the low-level change handler) shared by every
 * quote-shaped form. Callers own their own state/dispatch and pass in the
 * slice of fields this needs plus the setters that write back to it, so the
 * same rules apply whether the state lives in ApiContext or
 * MyFavoritePlanFormContext.
 */
export function useCoverageSelection({
    selectedBenefits,
    selectedBenefitsIds,
    selectedBenefitsNames,
    dependants = [],
    addDependant = false,
    country,
    isCountryResidenceDeductible = false,
    hasLoadedQuoteContext = false,
    isCountryResidenceDeductibleFlagPresent = false,
    changeBenefitValue,
    changeBenefitNameValue,
    changeBenefitIdValue,
}) {
    const benefits = useBenefits();
    const [ignoredBenefits, setIgnoredBenefits] = useState([]);

    const deductibleIsoNumber = isLegacyQuotedContext({ hasLoadedQuoteContext, isCountryResidenceDeductibleFlagPresent })
        ? null
        : country;
    const deductibleOptionsQuery = useDeductibleOptions(deductibleIsoNumber);

    const getAllBenefits = () => {
        return [...benefits.structuralData, ...benefits.regularData];
    };

    const calculateIgnored = () => {
        const ignored = [];
        const ruleContext = { selectedBenefitsIds, dependants, country, addDependant };

        benefitOptions.forEach(i => {
            let proceed = false;
            if (i.ignored instanceof Function) {
                proceed = i.ignored(ruleContext);
            } else {
                proceed = i.ignored;
            }
            if (proceed) {
                ignored.push(i.id);
            }
        });

        if (selectedBenefitsIds && selectedBenefitsIds.maxcovpipoly !== undefined) {
            const valueCompare = getValueByBenefitId({ selectedBenefitsNames }, 'maxcovpipoly');

            const congenitValues = filterBenefitsByBenefitId(getAllBenefits(), valueCompare, 'congenit', true);
            ignored.push(...congenitValues);

            const organValues = filterBenefitsByBenefitId(getAllBenefits(), valueCompare, 'orgtrans', true);
            ignored.push(...organValues);
        }

        const deductibleIgnored = getDeductibleIgnoredOptions(
            deductibleOptionsQuery.data ?? null,
            selectedBenefitsIds && selectedBenefitsIds.geogrcov,
            selectedBenefitsIds,
            getAllBenefits(),
            isCountryResidenceDeductible
        );
        ignored.push(...deductibleIgnored);

        if (country === "76") {
            ignored.push("wwltd");
        }

        if (addDependant && hasDependantInCountry(dependants, "152")) {
            const valueCompare = 5000;
            const ignoreDedUsaValues = filterBenefitsByBenefitId(getAllBenefits(), valueCompare, 'dedusa');
            ignored.push(...ignoreDedUsaValues);
            const ignoreDedNonUsaValues = filterBenefitsByBenefitId(getAllBenefits(), valueCompare, 'dednonusa');
            ignored.push(...ignoreDedNonUsaValues);
        }

        setIgnoredBenefits(ignored);
    };

    useEffect(() => {
        calculateIgnored();
    }, [selectedBenefitsIds, dependants, country, addDependant, isCountryResidenceDeductible, deductibleOptionsQuery.data]);

    const isPreferred = (id) => {
        return preferreds.includes(id);
    };

    const handleOnChangeBenefit = async (e, meta = null) => {
        const name = e?.target?.name ?? meta?.name;
        const value = e?.target?.value ?? e?.value;
        let text = meta?.text;
        let id = meta?.id;
        if (e?.target?.options?.length) {
            text = e.target?.options[e.target.selectedIndex]?.dataset?.description ?? meta?.text;
            id = e.target?.options[e.target.selectedIndex]?.dataset?.id ?? meta?.id;
        }

        changeBenefitValue(name, value);
        changeBenefitNameValue(name, text);
        changeBenefitIdValue(name, id);
    };

    const calculateSelectedItem = (benefit_, allBenefits) => {
        const benefit = benefit_["factorId.id"];
        const preferred = allBenefits.filter((i) => i["factorId.id"] === benefit)
            .filter((i) => !ignoredBenefits.includes(i.id))
            .find((i) => isPreferred(i.id));
        let selected = null;
        if (preferred) selected = preferred;
        if (!preferred) selected = !ignoredBenefits.includes(benefit_.id) && benefit_;

        const value = selected?.factor;

        if (value) {
            const name = benefit;
            handleOnChangeBenefit({ target: { value, name } }, { text: selected?.description, id: selected?.id });
        }
    };

    const calculateSelected = () => {
        const allBenefits = getAllBenefits();
        allBenefits.forEach((benefit_) => {
            const benefit = benefit_["factorId.id"];
            if (!selectedBenefits[benefit]) return;
            if (arrayIncludesNumber(ignoredBenefits, selectedBenefitsIds[benefit])) {
                calculateSelectedItem(benefit_, allBenefits);
            }
        });
    };

    useEffect(() => {
        calculateSelected();
    }, [ignoredBenefits]);

    return {
        benefits,
        deductibleOptionsQuery,
        ignoredBenefits,
        setIgnoredBenefits,
        isPreferred,
        preferreds,
        getAllBenefits,
        handleOnChangeBenefit,
        calculateSelectedItem,
    };
}
